(() => {
  const main = document.getElementById('MainContent');
  if (!main || window.NSScrollReveal || !('IntersectionObserver' in window) ||
      !window.CSS?.supports('translate', '0 28px')) return;

  const candidates = [
    '[data-ns-scroll-reveal]', '[data-ns-reveal]',
    '.ns-catalog-heading', '.ns-color-heading > div', '.ns-cat-photo > a',
    '.ns-video-head', '.ns-video-rail', '.ns-rest-story-inner',
    '.ns-club-home-copy', 'ns-club-journey', '.ns-club-steps > li',
    '.ns-rest-benefits-head', '.ns-rest-benefits-list > li', '.ns-close-inner',
    '.ns-pack-catalog > h2', '.ns-pack-card-photo',
    '.ns-classic-moments__head', '.ns-classic-moments__rail',
    '.ns-classic-featured-review', '.ns-photo-reviews__head', '.ns-photo-review',
    '.ns-classic-complements > h2', '.ns-classic-complement__photo',
    '.ns-routine-photo', '.ns-reference-service-details',
    '.rich-text__blocks', '.image-with-text__content', '.image-with-text__media',
    '.multicolumn-card', '.article-card-wrapper'
  ].join(',');
  const excluded = [
    '[data-ns-no-reveal]', '[hidden]', '[inert]', 'dialog', '[role="dialog"]',
    'header-component', '.header-wrapper', '.ns-hero', '.ns-rest-hero',
    '.ns-packs-showcase', '.banner', '.slideshow', 'media-gallery',
    '.product__media-wrapper', 'product-form', 'variant-selects', 'quantity-input',
    '.product-form', '.ns-classic-purchase', '.ns-routine-purchase',
    'cart-drawer', 'cart-items', '.cart', '.ns-cart', '.ns-buy-dialog'
  ].join(',');
  const purchase = 'product-form,variant-selects,quantity-input,form[action*="/cart"],.product-form,.ns-classic-purchase,.ns-routine-purchase,dialog,[role="dialog"]';
  const owned = '.scroll-trigger,ns-routine-reveal';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const editor = Boolean(window.Shopify?.designMode);
  const seen = new WeakSet();
  const pending = new Set();
  const selected = new Set();
  const queued = new Set();
  let observer;
  let enabled = false;
  let frame = 0;
  let resizeFrame = 0;

  function reveal(element, immediately = false) {
    pending.delete(element);
    observer?.unobserve(element);
    if (immediately) element.classList.remove('ns-scroll-reveal');
    element.removeAttribute('data-ns-scroll-pending');
    element.setAttribute('data-ns-scroll-revealed', '');
  }

  function showAll() {
    enabled = false;
    observer?.disconnect();
    document.documentElement.removeAttribute('data-ns-scroll-reveal-active');
    pending.forEach(element => reveal(element, true));
  }

  function createObserver() {
    observer?.disconnect();
    const inset = Math.round(Math.min(140, window.innerHeight * .14));
    observer = new IntersectionObserver(entries => {
      try {
        entries.forEach(entry => {
          if (entry.isIntersecting) reveal(entry.target);
        });
      } catch (_) { showAll(); }
    }, { threshold: .06, rootMargin: `0px 0px -${inset}px 0px` });
    pending.forEach(element => observer.observe(element));
  }

  function safe(element) {
    const owner = element.closest(owned);
    if (element.closest(excluded) || element.querySelector(purchase) ||
        (owner && !owner.matches('.product__info-wrapper')) || element.querySelector(owned) ||
        element.querySelector('h1,img[fetchpriority="high"],img[loading="eager"]') ||
        element.parentElement?.closest('.ns-scroll-reveal')) return false;
    const style = getComputedStyle(element);
    if (style.position === 'sticky' || style.position === 'fixed') return false;
    return ![...element.querySelectorAll('[class*="sticky"],[style*="sticky"]')]
      .some(child => getComputedStyle(child).position === 'sticky');
  }

  function register(root) {
    if (!enabled || !(root instanceof Element) || !main.contains(root)) return;
    const elements = root.matches(candidates) ? [root, ...root.querySelectorAll(candidates)] : [...root.querySelectorAll(candidates)];
    const additions = [];
    elements.forEach(element => {
      if (seen.has(element) || !safe(element)) return;
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      seen.add(element);
      const section = element.closest('.shopify-section');
      if (rect.top < window.innerHeight + 16 || rect.height > window.innerHeight * 1.4 ||
          element.contains(document.activeElement) || element.matches(':target') ||
          (editor && section && selected.has(section.id))) return;
      if (!additions.some(parent => parent.contains(element))) additions.push(element);
    });
    additions.forEach(element => {
      pending.add(element);
      element.classList.add('ns-scroll-reveal');
      element.setAttribute('data-ns-scroll-pending', '');
      observer.observe(element);
    });
  }

  function queue(root = main) {
    if (!enabled || !(root instanceof Element) || !main.contains(root)) return;
    queued.add(root);
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      try {
        const roots = [...queued];
        queued.clear();
        roots.filter(root => !roots.some(parent => parent !== root && parent.contains(root)))
          .forEach(register);
      } catch (_) { showAll(); }
    });
  }

  function expose(target) {
    if (!(target instanceof Element)) return;
    pending.forEach(element => {
      if (element === target || element.contains(target) || target.contains(element)) reveal(element, true);
    });
  }

  function hashTarget() {
    try { return document.getElementById(decodeURIComponent(location.hash.slice(1))); }
    catch (_) { return null; }
  }

  function activate() {
    if (motion.matches) { showAll(); return; }
    enabled = true;
    createObserver();
    document.documentElement.setAttribute('data-ns-scroll-reveal-active', '');
    document.documentElement.toggleAttribute('data-ns-scroll-editor', editor);
    try { register(main); expose(hashTarget()); } catch (_) { showAll(); }
  }

  window.NSScrollReveal = { refresh: queue };
  document.addEventListener('focusin', event => expose(event.target));
  document.addEventListener('click', event => {
    const anchor = event.target.closest?.('a[href*="#"]');
    if (!anchor) return;
    try {
      const url = new URL(anchor.href, location.href);
      if (url.origin === location.origin && url.pathname === location.pathname && url.search === location.search)
        expose(document.getElementById(decodeURIComponent(url.hash.slice(1))));
    } catch (_) { }
  });
  main.addEventListener('transitionend', event => {
    if (event.propertyName === 'opacity' && !pending.has(event.target))
      event.target.classList.remove('ns-scroll-reveal');
  });
  document.addEventListener('toggle', event => {
    if (event.target.open) queue(event.target);
  }, true);
  document.addEventListener('load', event => {
    if (event.target instanceof Element) queue(event.target.closest(candidates) || event.target);
  }, true);
  window.addEventListener('resize', () => {
    if (resizeFrame || !enabled) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      createObserver();
    });
  }, { passive: true });
  window.addEventListener('hashchange', () => expose(hashTarget()));
  window.addEventListener('pageshow', () => expose(hashTarget()));
  motion.addEventListener('change', activate);
  document.addEventListener('shopify:section:load', event => queue(event.target));
  const selectSection = event => {
    const section = event.target.closest('.shopify-section') || event.target;
    selected.add(section.id);
    expose(section);
  };
  document.addEventListener('shopify:section:select', selectSection);
  document.addEventListener('shopify:block:select', selectSection);
  document.addEventListener('shopify:section:deselect', event => {
    selected.delete((event.target.closest('.shopify-section') || event.target).id);
  });
  new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node instanceof Element) queue(node);
    }));
  }).observe(main, { childList: true, subtree: true });
  activate();
})();
